import React from 'react';

import PositionsTableRow, { PayRate, VenueAllocation, departmentPath, formatListDate, positionTotal } from './PositionsTableRow';
import RecruitmentStatusSelect from './RecruitmentStatusSelect';
import { COLUMN_BUDGET_CODE, COLUMN_EMPLOYEE_NUMBER, COLUMN_END_DATE, COLUMN_UNIQUE_ID, COLUMN_VENUE_PERCENT } from './usePositionsColumns';

import { ZERO_DASH, formatFigure } from 'Helpers/formatService';

import CheckboxInput from 'CommonComponents/CheckboxInput';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

/**
 * Positions identical in everything the list compares, as one row. The shared fields come
 * from the first member; Total cost and Venue allocation are the members' sum. Expanded,
 * the members follow as ordinary, indented rows.
 */
export default function PositionsGroupRow({
    group,
    expanded,
    onToggleExpanded,
    selectedIds,
    onSelectIds,
    statuses,
    statusEditable,
    statusSaving,
    showRecruitmentStatus,
    rowProps,
}) {
    const { members } = group;
    const lead = members[0];
    const { columns, showVenues, venues, types, selectable } = rowProps;

    const selectedCount = members.filter((member) => selectedIds.has(member.id)).length;
    const memberIds = members.map((member) => member.id);

    const statusIds = new Set(members.map((member) => member.recruitment_status_id ?? null));
    const mixed = showRecruitmentStatus && statusIds.size > 1;
    const editableMembers = members.filter((member) => member.can.update);

    const total = members.reduce((sum, member) => sum + positionTotal(member), 0);
    const allocationTotal = lead.allocation_totals
        ? { total: members.reduce((sum, member) => sum + (Number(member.allocation_totals?.total) || 0), 0), percentage: lead.allocation_totals.percentage }
        : null;

    const subtitle = departmentPath(lead);
    const Chevron = expanded ? ChevronDownIcon : ChevronRightIcon;

    return (
        <>
            <tr className="tw-relative" data-position-group={group.id}>
                <td>
                    {selectable && (
                        <CheckboxInput
                            aria-label={$t('Select {{name}}', { name: lead.name })}
                            checked={selectedCount === members.length}
                            indeterminate={selectedCount > 0 && selectedCount < members.length}
                            onChange={(e) => onSelectIds(memberIds, e.target.checked)}
                        />
                    )}
                </td>
                <td className="!tw-whitespace-normal">
                    <button
                        type="button"
                        className="tw-inline-flex tw-items-center tw-gap-x-1.5 tw-font-semibold tw-text-blue tw-text-start"
                        aria-expanded={expanded}
                        onClick={onToggleExpanded}
                    >
                        <Chevron className="tw-w-2 tw-h-2 tw-shrink-0" aria-hidden="true" />
                        {lead.name}
                        <span className="tw-rounded-[10px] tw-bg-blue-25 tw-px-[7px] tw-py-px tw-text-[11px] tw-font-semibold tw-text-blue" aria-hidden="true">
                            {members.length}
                        </span>
                        <span className="tw-sr-only">{$t('{{count}} positions', { count: members.length })}</span>
                    </button>
                    {!!subtitle && <div className="tw-text-2xs tw-text-charcoal-50">{subtitle}</div>}
                </td>
                <td>{types[lead.type]}</td>
                <td>
                    <RecruitmentStatusSelect
                        status={showRecruitmentStatus && !mixed ? lead.recruitment_status : null}
                        statuses={statuses}
                        mixed={mixed}
                        editable={statusEditable && editableMembers.length > 0}
                        disabled={statusSaving}
                        onChange={(statusId) =>
                            rowProps.onStatusChange(
                                editableMembers.map((member) => member.id),
                                statusId,
                            )
                        }
                    />
                </td>
                <td className="inbud-date-cell">{formatListDate(lead.start_date)}</td>
                {columns[COLUMN_END_DATE] && <td className="inbud-date-cell">{formatListDate(lead.end_date)}</td>}
                <td>
                    <PayRate position={lead} />
                </td>
                <td className="inbud-numeric-cell">{formatFigure(total)}</td>
                <td>{ZERO_DASH}</td>
                <td>{lead.supervisor?.unique_id}</td>
                {columns[COLUMN_UNIQUE_ID] && <td />}
                {columns[COLUMN_BUDGET_CODE] && <td />}
                {columns[COLUMN_EMPLOYEE_NUMBER] && <td />}
                <td className="inbud-numeric-cell">
                    <VenueAllocation allocation={allocationTotal} showPercent={columns[COLUMN_VENUE_PERCENT]} />
                </td>
                {showVenues &&
                    !!venues?.length &&
                    lead.all_venues?.map((venue) => (
                        <td key={venue.id} className="inbud-numeric-cell !tw-bg-white">
                            <VenueAllocation
                                allocation={
                                    venue.position_allocation && {
                                        total: (Number(venue.position_allocation.total) || 0) * members.length,
                                        percentage: venue.position_allocation.percentage,
                                    }
                                }
                                showPercent={columns[COLUMN_VENUE_PERCENT]}
                            />
                        </td>
                    ))}
                <td />
            </tr>
            {expanded &&
                members.map((member) => (
                    <PositionsTableRow
                        key={member.id}
                        {...rowProps}
                        position={member}
                        allVenues={lead.all_venues}
                        indented
                        selected={selectedIds.has(member.id)}
                        statuses={statuses}
                        statusEditable={statusEditable}
                        statusSaving={statusSaving}
                        showRecruitmentStatus={showRecruitmentStatus}
                        onSelect={(checked) => onSelectIds([member.id], checked)}
                    />
                ))}
        </>
    );
}
